import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { getUserRoles, hasPermission, PERMISSIONS } from '../utils/rbac';
import VideoPlayerModal from './VideoPlayerModal';
import './Videos.css';

const Videos = () => {
    const { user, isAdmin } = useContext(AuthContext);
    const [userRoles, setUserRoles] = useState([]);
    const [userData, setUserData] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const fallbackUnsubscribeRef = useRef(null);

    // Fetch user data and roles
    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribe = onSnapshot(
            doc(db, 'users', user.uid),
            (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setUserData(data);
                    setUserRoles(getUserRoles({ ...data, isAdmin }));
                }
            }
        );

        return () => unsubscribe();
    }, [user, isAdmin]);

    // Fetch videos from video_feed collection
    useEffect(() => {
        const videosRef = collection(db, 'video_feed');
        const q = query(videosRef, orderBy('createdAt', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const videoList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setVideos(videoList);
            setLoading(false);
        }, (error) => {
            // If ordering fails (e.g., no index or createdAt field missing), 
            // fetch without order and sort client-side
            console.warn("Could not order by createdAt, fetching all videos:", error);
            const fallbackUnsubscribe = onSnapshot(videosRef, (snapshot) => {
                const videoList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                // Sort by createdAt if available, otherwise by document ID
                videoList.sort((a, b) => {
                    const aDate = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
                    const bDate = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
                    return bDate - aDate;
                });
                setVideos(videoList);
                setLoading(false);
            });
            fallbackUnsubscribeRef.current = fallbackUnsubscribe;
        });

        return () => {
            unsubscribe();
            if (fallbackUnsubscribeRef.current) {
                fallbackUnsubscribeRef.current();
            }
        };
    }, []);

    const canViewProVideos = hasPermission(userRoles, PERMISSIONS.VIEW_PRO_CONTENT);
    const purchasedVideoIds = userData?.purchasedVideoIds || [];
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const formatPrice = (value) => {
        if (value === undefined || value === null || value === '') return '0.00';
        const numeric = Number(value);
        return Number.isNaN(numeric) ? '0.00' : numeric.toFixed(2);
    };

    const handleWatch = (video) => {
        setSelectedVideo(video);
        setIsVideoModalOpen(true);
    };

    const handleCloseVideoModal = () => {
        setIsVideoModalOpen(false);
        setSelectedVideo(null);
    };

    if (!canViewProVideos) {
        return (
            <div className="videos-container">
                <h2>"VIDEOS" Coming Soon! To a theater near you (your phone)</h2>
            </div>
        );
    }

    return (
        <div className="videos-container">
            <h1>Feed</h1>
            
            {loading ? (
                <p>Loading videos...</p>
            ) : videos.length === 0 ? (
                <p>No videos available yet. Check back soon!</p>
            ) : (
                <div className="videos-grid">
                    {videos.map((video) => {
                        const isPurchased = purchasedVideoIds.includes(video.id);
                        const isActive = video.isActive !== false; // Default to true if not set
                        const priceDisplay = formatPrice(video.price);
                        
                        if (!isActive) return null; // Don't show inactive videos
                        
                        return (
                            <div key={video.id} className="video-card">
                                {video.thumbnail && (
                                    <div className="video-thumbnail">
                                        <img src={video.thumbnail} alt={video.title} />
                                    </div>
                                )}
                                <div className="video-info">
                                    <h3>{video.title || 'Untitled Video'}</h3>
                                    {video.description && (
                                        <p className="video-description">{video.description}</p>
                                    )}
                                    <div className="video-meta">
                                        <span className="video-price">${priceDisplay}</span>
                                        {video.createdAt && (
                                            <span className="video-date">
                                                {new Date(video.createdAt.toDate?.() || video.createdAt).toLocaleDateString()}
                                            </span>
                                        )}
                                        {isAdmin && (
                                            <span className="video-purchase-count">
                                                🛒 {(video.purchaserEmails || []).length} purchased
                                            </span>
                                        )}
                                    </div>
                                    <div className="video-actions">
                                        {isPurchased ? (
                                            <button
                                                className="btn-watch"
                                                onClick={() => handleWatch(video)}
                                            >
                                                Watch
                                            </button>
                                        ) : (
                                            <button
                                                className="btn-purchase"
                                                disabled
                                            >
                                                Purchases closed
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            
            <VideoPlayerModal
                video={selectedVideo}
                isOpen={isVideoModalOpen}
                onClose={handleCloseVideoModal}
            />
        </div>
    );
};

export default Videos;
